import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Article } from '@/lib/types';
import type { VehicleReview, VehicleSegment, GalleryImage } from '@/lib/vehicleReviewTypes';
import type { Event } from '@/lib/types';
import type { Json } from '@/integrations/supabase/types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const CONTENT_API_URL = `${SUPABASE_URL}/functions/v1/content-api`;

// Helper to call content API with timeout and fallback
// skipCache=true adds a cache-busting param to bypass edge cache
async function fetchFromContentApi<T>(
  endpoint: string,
  params: Record<string, string> = {},
  timeoutMs = 5000,
  skipCache = false
): Promise<{ data: T | null; fromCache: boolean }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // Add cache-busting param when skipCache is true
    const allParams = skipCache 
      ? { ...params, _t: String(Date.now()) }
      : params;
    const queryString = new URLSearchParams(allParams).toString();
    const url = `${CONTENT_API_URL}/${endpoint}${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // Tell CDN to skip cache when needed
        ...(skipCache ? { 'Cache-Control': 'no-cache' } : {}),
      },
      signal: controller.signal,
      // Force fresh fetch
      cache: skipCache ? 'no-store' : 'default',
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (import.meta.env.DEV) {
        console.warn(`Content API returned ${response.status} for ${endpoint}`);
      }
      return { data: null, fromCache: false };
    }

    const data = await response.json();
    return { data, fromCache: !skipCache };
  } catch (error) {
    clearTimeout(timeoutId);
    if (import.meta.env.DEV) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`Content API timeout for ${endpoint}`);
      } else {
        console.warn(`Content API error for ${endpoint}:`, error);
      }
    }
    return { data: null, fromCache: false };
  }
}

// ============ ARTICLES ============

interface UseArticlesOptions {
  contentType?: string;
  limit?: number;
  featured?: boolean;
  hero?: boolean;
  enabled?: boolean;
}

export function useCachedArticles(options: UseArticlesOptions = {}) {
  const { contentType, limit = 20, featured, hero, enabled = true } = options;

  return useQuery({
    queryKey: ['cached-articles', options],
    queryFn: async () => {
      // Try content API first
      const params: Record<string, string> = { limit: String(limit) };
      if (contentType) params.contentType = contentType;
      if (featured) params.featured = 'true';
      if (hero) params.hero = 'true';

      const { data: cachedData } = await fetchFromContentApi<Article[]>('articles', params);
      
      if (cachedData) {
        return cachedData;
      }

      // Fallback to direct Supabase query
      let query = supabase
        .from('articles')
        .select(`
          id, slug, title, subtitle, content_type, published_at, 
          thumbnail_url, hero_image_url, read_time_minutes, is_featured, is_hero,
          article_categories (categories (id, name, slug))
        `)
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(limit);

      if (contentType) query = query.eq('content_type', contentType);
      if (featured) query = query.eq('is_featured', true);
      if (hero) query = query.eq('is_hero', true);

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((article: Record<string, unknown>) => ({
        ...article,
        categories: (article.article_categories as Array<{ categories: unknown }>)?.map(
          (ac) => ac.categories
        ) || [],
      })) as Article[];
    },
    enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useCachedArticle(slug: string) {
  return useQuery({
    queryKey: ['cached-article', slug],
    queryFn: async () => {
      // Try content API first
      const { data: cachedData } = await fetchFromContentApi<Article>('article', { slug });
      
      if (cachedData) {
        return cachedData;
      }

      // Fallback to direct Supabase query
      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          article_categories (categories (id, name, slug)),
          article_tags (tags (id, name, slug)),
          article_oems (oems (id, name, slug)),
          article_vehicles (vehicles (id, make, model, year, segment, powertrain))
        `)
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (error) throw error;

      return {
        ...data,
        categories: data.article_categories?.map((ac: { categories: unknown }) => ac.categories) || [],
        tags: data.article_tags?.map((at: { tags: unknown }) => at.tags) || [],
        oems: data.article_oems?.map((ao: { oems: unknown }) => ao.oems) || [],
        vehicles: data.article_vehicles?.map((av: { vehicles: unknown }) => av.vehicles) || [],
      } as Article;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============ VEHICLE REVIEWS ============

function parseGalleryImages(json: Json | null | undefined): GalleryImage[] {
  if (!json || !Array.isArray(json)) return [];
  return json as unknown as GalleryImage[];
}

interface UseVehicleReviewsOptions {
  make?: string;
  segment?: VehicleSegment;
  limit?: number;
  enabled?: boolean;
}

export function useCachedVehicleReviews(options: UseVehicleReviewsOptions = {}) {
  const { make, segment, limit = 20, enabled = true } = options;

  return useQuery({
    queryKey: ['cached-vehicle-reviews', options],
    queryFn: async () => {
      // Try content API first
      const params: Record<string, string> = { limit: String(limit) };
      if (make) params.make = make;
      if (segment) params.segment = segment;

      const { data: cachedData } = await fetchFromContentApi<VehicleReview[]>('vehicle-reviews', params);
      
      if (cachedData) {
        return cachedData;
      }

      // Fallback to direct Supabase query
      let query = supabase
        .from('vehicle_reviews')
        .select(`
          id, slug, title_h1, make, model, model_year, trim_reviewed,
          segment, rating, hero_image_url, bottom_line, published_at,
          pros, cons, ideal_for, price_band
        `)
        .eq('status', 'published')
        .order('published_at', { ascending: false, nullsFirst: false })
        .limit(limit);

      if (make) query = query.ilike('make', make);
      if (segment) query = query.eq('segment', segment);

      const { data, error } = await query;
      if (error) throw error;

      return (data || []) as unknown as VehicleReview[];
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useCachedVehicleReview(slug: string) {
  return useQuery({
    queryKey: ['cached-vehicle-review', slug],
    queryFn: async () => {
      // Try content API first
      const { data: cachedData } = await fetchFromContentApi<VehicleReview>('vehicle-review', { slug });
      
      if (cachedData) {
        return {
          ...cachedData,
          gallery_images: parseGalleryImages(cachedData.gallery_images as unknown as Json),
        };
      }

      // Fallback to direct Supabase query
      const { data, error } = await supabase
        .from('vehicle_reviews')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (error) throw error;

      return {
        ...data,
        gallery_images: parseGalleryImages(data.gallery_images),
      } as VehicleReview;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

// ============ EVENTS ============

interface UseEventsOptions {
  upcoming?: boolean;
  limit?: number;
  region?: string;
  eventType?: string;
  enabled?: boolean;
}

export function useCachedEvents(options: UseEventsOptions = {}) {
  const { upcoming = true, limit = 20, region, eventType, enabled = true } = options;

  return useQuery({
    queryKey: ['cached-events', options],
    queryFn: async () => {
      // Try content API first
      const params: Record<string, string> = { 
        limit: String(limit),
        upcoming: String(upcoming),
      };
      if (region) params.region = region;
      if (eventType) params.eventType = eventType;

      const { data: cachedData } = await fetchFromContentApi<Event[]>('events', params);
      
      if (cachedData) {
        return cachedData;
      }

      // Fallback to direct Supabase query
      let query = supabase.from('events').select('*');

      if (upcoming) {
        query = query.gte('event_date', new Date().toISOString());
      }

      query = query.order('event_date', { ascending: true }).limit(limit);

      const { data, error } = await query;
      if (error) throw error;

      let events = data as Event[];

      if (region) {
        events = events.filter(e => e.region === region);
      }
      if (eventType) {
        events = events.filter(e => e.event_type === eventType);
      }

      return events;
    },
    enabled,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useCachedEvent(slug: string) {
  return useQuery({
    queryKey: ['cached-event', slug],
    queryFn: async () => {
      // Try content API first
      const { data: cachedData } = await fetchFromContentApi<Event>('event', { slug });
      
      if (cachedData) {
        return cachedData;
      }

      // Fallback to direct Supabase query
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data as Event;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============ HERO ARTICLE ============

export function useCachedHeroArticle() {
  return useQuery({
    queryKey: ['cached-hero-article'],
    queryFn: async () => {
      // For hero content, go directly to Supabase for real-time accuracy
      const { data: heroData } = await supabase
        .from('articles')
        .select(`
          *,
          article_categories (categories (id, name, slug))
        `)
        .eq('status', 'published')
        .eq('is_hero', true)
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (heroData) {
        return {
          ...heroData,
          categories: heroData.article_categories?.map((ac: { categories: unknown }) => ac.categories) || [],
        } as Article;
      }

      // Fallback to featured if no hero
      const { data: fallback } = await supabase
        .from('articles')
        .select(`
          *,
          article_categories (categories (id, name, slug))
        `)
        .eq('status', 'published')
        .eq('is_featured', true)
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!fallback) return null;

      return {
        ...fallback,
        categories: fallback.article_categories?.map((ac: { categories: unknown }) => ac.categories) || [],
      } as Article;
    },
    staleTime: 1000 * 30, // 30 seconds - short stale time for hero
    refetchOnWindowFocus: true, // Refetch when user comes back to tab
  });
}
